import React, { useState, useEffect, useRef } from 'react';

// Reusable animated count-up hook or component
const CountUp = ({ target, suffix = "", decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          let startTime = null;
          const start = 0;
          const end = parseFloat(target);
          const duration = 2000;

          const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const progressPercentage = Math.min(progress / duration, 1);

            const easeOutQuad = (t) => t * (2 - t);
            const easedProgress = easeOutQuad(progressPercentage);

            const currentValue = start + easedProgress * (end - start);
            setCount(currentValue);

            if (progress < duration) {
              requestAnimationFrame(animate);
            } else {
              setCount(end);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [target]);

  const formattedCount = count.toFixed(decimals);
  const formattedString = decimals === 0
    ? parseInt(formattedCount, 10).toLocaleString()
    : formattedCount;

  return <span ref={elementRef}>{formattedString}{suffix}</span>;
};

const STATS = [
  {
    num: 90,
    suffix: "+",
    label: "Years of Excellence",
    icon: (
      <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <path d="M12 14l1 1.5 2-.5-1 2 1.5 1.5-2-.5-1.5 2 .5-2.5-2-1 2.5-.5z" />
      </svg>
    )
  },
  {
    num: 75000,
    suffix: "+",
    label: "Number of Calls Per Day",
    icon: (
      <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <circle cx="12" cy="8" r="2.5" />
        <path d="M7 14c0-1.2 1.8-2 5-2s5 .8 5 2" />
      </svg>
    )
  },
  {
    num: 500,
    suffix: "+",
    label: "Projects Completed",
    icon: (
      <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="18" rx="2" ry="2" />
        <polyline points="8 10 5 12 8 14" />
        <polyline points="16 10 19 12 16 14" />
        <line x1="13" y1="8" x2="11" y2="16" />
      </svg>
    )
  },
  {
    num: 90,
    suffix: "%",
    label: "Client Retention Rate",
    icon: (
      <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="7" y1="8" x2="11" y2="8" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="7" y1="16" x2="13" y2="16" />
        <circle cx="16" cy="8" r="1.5" />
      </svg>
    )
  }
];

function StatsGrid() {
  return (
    <section className="stats-grid-section">
      <div className="container stats-grid-container">
        {/* Left Column: Content */}
        <div className="stats-grid-left">
          <h2 className="stats-grid-title">
            Driven by Numbers,<br />
            <span className="highlight-red">Proven by Results</span>
          </h2>
          <p className="stats-grid-desc">
            We are committed to delivering top-tier operational efficiency and tech-driven BPO solutions that empower brands worldwide. Our transparent approach and dedicated professional teams ensure consistent, high-impact results for every partner.
          </p>

          <ul className="stats-grid-list">
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="list-check-icon">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Customized BPO & IT Workflow Integrations
            </li>
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="list-check-icon">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              24/7/365 Omnichannel Support Operations
            </li>
            <li>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="list-check-icon">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Enterprise-Grade Bandwidth & Data Protection
            </li>
          </ul>
        </div>

        {/* Right Column: 2x2 Counters Grid */}
        <div className="stats-grid-right">
          <div className="stats-box-layout">
            {STATS.map((stat, idx) => (
              <div className="stat-card" key={idx}>
                <div className="stat-card-top">
                  <div className="stat-icon-wrapper">{stat.icon}</div>
                  <span className="stat-card-number">
                    <CountUp target={stat.num} suffix={stat.suffix} />
                  </span>
                </div>
                <span className="stat-card-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default StatsGrid;
